// TODO: i18n — see Auth.js for pattern, replace hardcoded strings with t('namespace.key')
import { useState } from 'react'
import Input from './shared/Input'
import { createAppliance } from '../utils/applianceQueries'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', brown: '#2C1D14', muted: '#9A7B68',
}
const f = 'Georgia, serif'
const fs = 'Calibri, sans-serif'

const PROPERTY_TYPES = [
  { id: 'single_family', label: '🏠 Single Family' },
  { id: 'condo', label: '🏢 Condo' },
  { id: 'townhouse', label: '🏘️ Townhouse' },
  { id: 'multi_family', label: '🏗️ Multi-Family' },
]

const APPLIANCE_TYPES = [
  { id: 'refrigerator', emoji: '🧊', label: 'Refrigerator' },
  { id: 'washer', emoji: '🫧', label: 'Washer' },
  { id: 'dryer', emoji: '🌀', label: 'Dryer' },
  { id: 'dishwasher', emoji: '🍽️', label: 'Dishwasher' },
  { id: 'hvac', emoji: '❄️', label: 'HVAC' },
  { id: 'water_heater', emoji: '🚿', label: 'Water Heater' },
  { id: 'range', emoji: '🍳', label: 'Range / Oven' },
  { id: 'microwave', emoji: '📡', label: 'Microwave' },
]

const TOTAL_STEPS = 4

export default function Onboarding({ supabase, session, profile, homeId, onComplete }) {
  const [step, setStep] = useState(1)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    address: '', city: '', state: 'CA', zip: '',
    property_type: '', year_built: '', bedrooms: '', bathrooms: '',
  })
  const [firstAppliance, setFirstAppliance] = useState({ type: '', name: '', make: '', model: '' })
  const [inviteEmail, setInviteEmail] = useState('')

  const setF = (k, v) => setFormData(p => ({ ...p, [k]: v }))
  const setA = (k, v) => setFirstAppliance(p => ({ ...p, [k]: v }))

  const handleNext = async () => {
    setSaving(true)
    try {
      if (step === 2) {
        if (!formData.address.trim() || !formData.city.trim()) {
          alert('Please enter your address and city')
          setSaving(false)
          return
        }
        await supabase.from('homes').update({
          address: formData.address.trim(),
          city: formData.city.trim(),
          state: formData.state || 'CA',
          zip: formData.zip || null,
          property_type: formData.property_type || null,
          year_built: formData.year_built ? parseInt(formData.year_built) : null,
          bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
          bathrooms: formData.bathrooms ? parseFloat(formData.bathrooms) : null,
        }).eq('id', homeId)
        supabase.functions.invoke('lookup-emergency-services', {
          body: { home_id: homeId, address: formData.address, city: formData.city, state: formData.state || 'CA' },
        }).catch(() => {})
        setStep(3)
      } else if (step === 3) {
        if (firstAppliance.type && firstAppliance.make.trim()) {
          await createAppliance(supabase, {
            homeId,
            type: firstAppliance.type,
            make: firstAppliance.make.trim(),
            model: firstAppliance.model.trim() || null,
            status: 'good',
            createdBy: session.user.id,
          })
        }
        setStep(4)
      } else if (step === 4) {
        if (inviteEmail && inviteEmail.includes('@')) {
          // Match the send-invite Edge Function contract exactly (same shape
          // InviteModal.js uses). The prior payload was invitee_email /
          // inviter_name — those fields don't exist on the EF, so the insert
          // silently dropped the relation/role and the email never went out.
          // Onboarding's framing is "invite a family member," so default
          // relation=family_member and role=member.
          const fullAddress = [
            formData.address?.trim(),
            formData.city?.trim(),
            formData.state || 'CA',
            formData.zip?.trim(),
          ].filter(Boolean).join(', ')
          const invitedByName = profile?.first_name?.trim()
            || session?.user?.email?.split('@')[0]
            || 'Someone'
          try {
            const { error: inviteErr } = await supabase.functions.invoke('send-invite', {
              body: {
                home_id: homeId,
                invited_by: session?.user?.id,
                invited_email: inviteEmail.trim().toLowerCase(),
                relation: 'family_member',
                role: 'member',
                invited_by_name: invitedByName,
                home_address: fullAddress || null,
              },
            })
            if (inviteErr) throw inviteErr
          } catch (err) {
            // Surface but don't block — onboarding should still complete even
            // if the invite couldn't go out (typo, network blip, etc.). The
            // user can retry from Settings.
            console.error('[Onboarding] send-invite failed', err)
            alert(`Couldn't send invite to ${inviteEmail.trim().toLowerCase()}. You can invite them later from Settings.`)
          }
        }
        await supabase.from('homes').update({ onboarding_complete: true }).eq('id', homeId)
        onComplete()
      }
    } catch (err) {
      console.error('Onboarding error:', err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: C.bg, zIndex: 500, display: 'flex', flexDirection: 'column', overflowY: 'auto', fontFamily: fs }}>

      {/* Header */}
      <div style={{ background: '#2C1D14', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <span style={{ fontFamily: f, fontSize: 18, color: '#FDFAF6', letterSpacing: 2 }}>CASAVIO</span>
        <span style={{ fontSize: 12, color: '#9A7B68' }}>Step {step} of {TOTAL_STEPS}</span>
      </div>

      {/* Progress bar */}
      <div style={{ height: 4, background: '#D4C5B5', flexShrink: 0 }}>
        <div style={{ height: '100%', width: `${(step / TOTAL_STEPS) * 100}%`, background: C.amber, transition: 'width 0.4s ease' }} />
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 24px 120px' }}>
        <div style={{ maxWidth: 480, margin: '0 auto' }}>

          {/* Step 1 — Welcome */}
          {step === 1 && (
            <div style={{ paddingTop: 20 }}>
              <div style={{ textAlign: 'center', marginBottom: 32 }}>
                <div style={{ fontSize: 52, marginBottom: 16 }}>🏠</div>
                <div style={{ fontFamily: f, fontSize: 24, color: C.brown, marginBottom: 12 }}>
                  Welcome to Casavio{profile?.first_name ? `, ${profile.first_name}` : ''}!
                </div>
                <div style={{ fontSize: 15, color: C.muted, lineHeight: 1.7 }}>
                  Let's set up your home in about 2 minutes.
                </div>
              </div>
              <div style={{ background: C.card, borderRadius: 12, padding: 20, marginBottom: 24 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.brown, marginBottom: 14 }}>What you'll get:</div>
                {[
                  { emoji: '📋', text: 'Complete maintenance schedule for all your appliances' },
                  { emoji: '🔒', text: 'Secure vault for warranties, manuals, and documents' },
                  { emoji: '🚨', text: 'Emergency contacts always ready when you need them' },
                  { emoji: '⭐', text: 'Home upgrade wishlist connected to local contractors' },
                  { emoji: '👨‍👩‍👧', text: 'Share your home with family — perfect for aging parents' },
                ].map(item => (
                  <div key={item.text} style={{ display: 'flex', gap: 12, marginBottom: 10, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 18, flexShrink: 0 }}>{item.emoji}</span>
                    <span style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 2 — Home Address */}
          {step === 2 && (
            <div>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontFamily: f, fontSize: 22, color: C.brown, marginBottom: 6 }}>📍 Your Home Address</div>
                <div style={{ fontSize: 14, color: C.muted }}>The foundation of your home profile</div>
              </div>
              <div style={{ background: C.card, borderRadius: 12, padding: 20 }}>
                <div style={{ marginBottom: 12 }}>
                  <Input label="Street address *" value={formData.address} onChange={v => setF('address', v)} placeholder="300 Harbor Drive" autoComplete="street-address" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 12 }}>
                  <Input label="City *" value={formData.city} onChange={v => setF('city', v)} placeholder="Novato" />
                  <Input label="ZIP" value={formData.zip} onChange={v => setF('zip', v)} placeholder="94945" />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: C.muted, marginBottom: 8, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', fontFamily: fs }}>Property Type</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {PROPERTY_TYPES.map(type => (
                      <div
                        key={type.id}
                        onClick={() => setF('property_type', type.id)}
                        style={{ padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, background: formData.property_type === type.id ? C.amber : C.bg, color: formData.property_type === type.id ? '#fff' : C.muted, border: formData.property_type === type.id ? 'none' : `1px solid ${C.border}` }}
                      >
                        {type.label}
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <Input label="Year built" value={formData.year_built} onChange={v => setF('year_built', v)} placeholder="1976" type="number" />
                  <Input label="Bedrooms" value={formData.bedrooms} onChange={v => setF('bedrooms', v)} placeholder="3" type="number" />
                  <Input label="Bathrooms" value={formData.bathrooms} onChange={v => setF('bathrooms', v)} placeholder="2" type="number" />
                </div>
              </div>
            </div>
          )}

          {/* Step 3 — First Appliance */}
          {step === 3 && (
            <div>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontFamily: f, fontSize: 22, color: C.brown, marginBottom: 6 }}>🔌 Add Your First Appliance</div>
                <div style={{ fontSize: 14, color: C.muted }}>Start with the one you care most about. Add more anytime.</div>
              </div>
              <div style={{ background: C.card, borderRadius: 12, padding: 20 }}>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 10, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', fontFamily: fs }}>Appliance Type</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 20 }}>
                  {APPLIANCE_TYPES.map(type => (
                    <div
                      key={type.id}
                      onClick={() => { setA('type', type.id); setA('name', type.label) }}
                      style={{ padding: '12px 8px', borderRadius: 8, cursor: 'pointer', textAlign: 'center', background: firstAppliance.type === type.id ? C.amber : C.bg, border: firstAppliance.type === type.id ? 'none' : `1px solid ${C.border}` }}
                    >
                      <div style={{ fontSize: 22 }}>{type.emoji}</div>
                      <div style={{ fontSize: 10, marginTop: 4, color: firstAppliance.type === type.id ? '#fff' : C.muted }}>{type.label}</div>
                    </div>
                  ))}
                </div>
                {firstAppliance.type && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <Input label="Brand / Make *" value={firstAppliance.make} onChange={v => setA('make', v)} placeholder="e.g. LG, Samsung, Whirlpool" />
                    <Input label="Model number (optional)" value={firstAppliance.model} onChange={v => setA('model', v)} placeholder="Found on the appliance label" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 4 — Invite Family */}
          {step === 4 && (
            <div>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontFamily: f, fontSize: 22, color: C.brown, marginBottom: 6 }}>👨‍👩‍👧 Invite a Family Member</div>
                <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.6 }}>
                  Share your home with a spouse, child, or family member. Optional — you can always do this later.
                </div>
              </div>
              <div style={{ background: C.card, borderRadius: 12, padding: 20, marginBottom: 16 }}>
                <Input label="Their email address" value={inviteEmail} onChange={setInviteEmail} type="email" placeholder="mom@email.com or spouse@email.com" />
                <div style={{ fontSize: 12, color: C.muted }}>They'll receive an invitation to join your home on Casavio</div>
              </div>
              <div style={{ background: '#FFF8E7', borderRadius: 12, padding: 16, marginBottom: 20, border: `1px solid ${C.amber}` }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.amber, marginBottom: 6 }}>👵 Great for aging parents</div>
                <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
                  Family members get a simplified view — just their tasks, emergency contacts, and a way to report problems. You stay in control of the full dashboard.
                </div>
              </div>
              <div style={{ background: '#F0FDF4', borderRadius: 12, padding: 16, border: '1px solid #16A34A' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#16A34A', marginBottom: 6 }}>🎉 You're almost done!</div>
                <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
                  Your home is set up. You can always add more appliances, fill in room details, and invite family members from within the app.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fixed bottom buttons */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, padding: '16px 24px', background: C.card, borderTop: `1px solid ${C.border}` }}>
        <div style={{ maxWidth: 480, margin: '0 auto' }}>
          <button
            onClick={step === 1 ? () => setStep(2) : handleNext}
            disabled={saving}
            style={{ width: '100%', background: C.amber, color: '#fff', border: 'none', borderRadius: 10, padding: '16px 0', fontFamily: f, fontSize: 16, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1, marginBottom: 8 }}
          >
            {saving ? 'Saving...' :
             step === 1 ? "Let's get started →" :
             step === 2 ? 'Save My Home →' :
             step === 3 ? (firstAppliance.type && firstAppliance.make ? 'Add Appliance →' : 'Skip for now →') :
             inviteEmail ? 'Send Invite & Finish 🎉' : 'Finish Setup 🎉'}
          </button>
          {step > 1 && (
            <button
              onClick={() => setStep(s => s - 1)}
              disabled={saving}
              style={{ width: '100%', background: 'none', border: 'none', color: C.muted, fontSize: 14, cursor: 'pointer', padding: '8px 0' }}
            >
              ← Back
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
